<script setup lang="ts">
import './assets/style.scss'
import { computed, onBeforeUnmount, ref, useAttrs, watch } from 'vue'
import { debounce } from 'lodash'
import { useTranslate } from '@/shared/lib'
import { VPreloader } from '@/shared/ui'
import { ADDRESS_SUGGEST_MIN_CHARS, useAddressSuggest } from '../model/api'
import { addressKindLabelKey } from '../model/kindLabel'
import type { AddressSuggestion } from '../model/types'

defineOptions({ inheritAttrs: false })

const value = defineModel<string>({ default: '' })
const emits = defineEmits<{ select: [AddressSuggestion] }>()

const attrs = useAttrs()
const { trs } = useTranslate()

const search = ref((value.value ?? '').trim())
const isOpen = ref(false)
const activeIndex = ref(-1)
const rootRef = ref<HTMLElement | null>(null)
const menuWidth = ref<number>()

const { items, isFetching, isError, isEnabled } = useAddressSuggest(search)

const isInteractive = computed(() => !attrs.disabled && !attrs.readonly)

/** Пустая строка означает «показываем результаты, а не заглушку». */
const stateMessage = computed(() => {
  if (!isEnabled.value) {
    return trs('itf.address_suggest_hint', { num: ADDRESS_SUGGEST_MIN_CHARS })
  }
  if (isError.value) return trs('itf.address_suggest_error')
  if (!items.value.length && !isFetching.value) return trs('itf.address_suggest_empty')

  return ''
})

const applySearch = debounce(() => {
  search.value = (value.value ?? '').trim()
}, 400)

function openMenu() {
  if (!isInteractive.value) return

  menuWidth.value = rootRef.value?.offsetWidth
  isOpen.value = true
}

function closeMenu() {
  isOpen.value = false
  activeIndex.value = -1
}

function onInput() {
  activeIndex.value = -1
  openMenu()
  applySearch()
}

function selectItem(item: AddressSuggestion) {
  // Отменяем отложенный запрос: текст уже заменён выбранным адресом.
  applySearch.cancel()
  value.value = item.title
  closeMenu()
  emits('select', item)
}

function moveActive(step: number) {
  if (!items.value.length) return

  const last = items.value.length - 1
  const next = activeIndex.value + step

  activeIndex.value = next < 0 ? last : next > last ? 0 : next
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' || event.key === 'Tab') {
    closeMenu()
    return
  }

  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault()
    openMenu()
    moveActive(event.key === 'ArrowDown' ? 1 : -1)
    return
  }

  if (event.key === 'Enter' && activeIndex.value >= 0) {
    event.preventDefault()
    selectItem(items.value[activeIndex.value])
  }
}

watch(items, () => {
  activeIndex.value = -1
})

onBeforeUnmount(() => applySearch.cancel())
</script>

<template>
  <div ref="rootRef" class="v-address-suggest">
    <VTextField
      v-bind="$attrs"
      v-model="value"
      autocomplete="off"
      @update:model-value="onInput"
      @focus="openMenu"
      @click="openMenu"
      @keydown="onKeydown"
    >
      <template #append-inner>
        <VPreloader v-if="isFetching" :size="20" />
      </template>
    </VTextField>

    <VMenu
      v-model="isOpen"
      activator="parent"
      location="bottom start"
      offset="4"
      :open-on-click="false"
      :close-on-content-click="false"
      :retain-focus="false"
      :min-width="menuWidth"
      :max-width="menuWidth"
      :z-index="1000000"
    >
      <VList class="v-address-suggest__list" lines="two" density="compact">
        <VListItem v-if="stateMessage" class="v-address-suggest__state" :title="stateMessage" />

        <template v-else>
          <VListItem
            v-for="(item, index) in items"
            :key="item.id"
            :active="index === activeIndex"
            class="v-address-suggest__item"
            @mousedown.prevent
            @click="selectItem(item)"
          >
            <template #title>{{ item.title }}</template>
            <template #subtitle>
              <span class="v-address-suggest__kind">{{ trs(addressKindLabelKey(item.kind)) }}</span>
            </template>
          </VListItem>
        </template>
      </VList>
    </VMenu>
  </div>
</template>
